import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import { notification } from "antd";
import { Strings } from "../../../../../dataProvider/localize";
import { getSwmsDraftVersionsV2, revertSwmsDraftVersionV2 } from "../../../../../actions/v2/JobSwmsTaskActions";
import styles from "./SwmsVersionHistoryModal.module.css";

const HistoryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor" className={styles.headerIcon}>
    <path d="M288 64c106 0 192 86 192 192S394 448 288 448c-65.2 0-122.9-32.5-157.6-82.3-10.1-14.5-30.1-18-44.6-7.9s-18 30.1-7.9 44.6C124.1 468.6 201 512 288 512 429.4 512 544 397.4 544 256S429.4 0 288 0C202.3 0 126.5 42.1 80 106.7L80 80c0-17.7-14.3-32-32-32S16 62.3 16 80l0 112c0 17.7 14.3 32 32 32l24.6 0c.5 0 1 0 1.5 0l86 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-38.3 0C154.9 102.6 217 64 288 64zm24 88c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 104c0 6.4 2.5 12.5 7 17l72 72c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-65-65 0-94.1z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" className={styles.closeBtnIcon}>
    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
  </svg>
);

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className={styles.warningIcon}>
    <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z" />
  </svg>
);

const RevertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" className={styles.revertIcon}>
    <path d="M88 256L232 256C241.7 256 250.5 250.2 254.2 241.2C257.9 232.2 255.9 221.9 249 215L202.3 168.3C277.6 109.7 386.6 115 455.8 184.2C530.8 259.2 530.8 380.7 455.8 455.7C380.8 530.7 259.3 530.7 184.3 455.7C174.1 445.5 165.3 434.4 157.9 422.7C148.4 407.8 128.6 403.4 113.7 412.9C98.8 422.4 94.4 442.2 103.9 457.1C113.7 472.7 125.4 487.5 139 501C239 601 401 601 501 501C601 401 601 239 501 139C406.8 44.7 257.3 39.3 156.7 122.8L105 71C98.1 64.2 87.8 62.1 78.8 65.8C69.8 69.5 64 78.3 64 88L64 232C64 245.3 74.7 256 88 256z" />
  </svg>
);

const getFirstValue = (values, fallback) => {
  const firstValue = values.find((value) => value !== undefined && value !== null && value !== "");
  return firstValue !== undefined && firstValue !== null ? firstValue : fallback;
};

const toArray = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (Array.isArray(payload.versions)) {
    return payload.versions;
  }

  if (Array.isArray(payload.history)) {
    return payload.history;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  return [];
};

const normalizeStatus = (status) => {
  return status ? `${status}`.trim().toUpperCase().replace(/\s+/g, "_") : "DRAFT";
};

const formatStatusLabel = (status) => {
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusClass = (status) => {
  const normalizedStatus = normalizeStatus(status);

  if (normalizedStatus === "APPROVED") {
    return styles.statusApproved;
  }

  if (normalizedStatus === "PENDING_APPROVAL" || normalizedStatus === "PENDING") {
    return styles.statusPending;
  }

  if (normalizedStatus === "REJECTED") {
    return styles.statusRejected;
  }

  if (normalizedStatus === "SUPERSEDED" || normalizedStatus === "ARCHIVED") {
    return styles.statusArchived;
  }

  return styles.statusDraft;
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return `${value}`;
  }

  return date.toLocaleDateString();
};

const getNumericVersion = (versionLabel) => {
  if (!versionLabel) {
    return null;
  }

  const match = `${versionLabel}`.match(/\d+/);
  if (!match) {
    return null;
  }

  const parsed = Number(match[0]);
  return Number.isNaN(parsed) ? null : parsed;
};

const getDefaultVersionNote = (isCurrentVersion) => {
  if (isCurrentVersion) {
    return "This is the current active SWMS version.";
  }

  return "This is a previous SWMS version available for review or revert.";
};

const normalizeVersions = (rawVersions) => {
  const normalizedItems = rawVersions.map((item, index) => {
    const rawLabel = item && item.version ? item.version : `v${index + 1}`;
    const versionLabel = /^\d/.test(`${rawLabel}`) ? `v${rawLabel}` : `${rawLabel}`;

    const status = item && item.status ? item.status : "DRAFT";

    const createdAt = item && item.createdAt ? item.createdAt : null;

    const createdBy = (item && item.createdByName) ? item.createdByName : "-";

    const note = getFirstValue([
      item && item.edit_text,
      item && item.edit_note,
      item && item.note,
      item && item.description
    ], null);

    return {
      key: item && (item.version_id || item.id || item.swms_form_id) ? `${item.version_id || item.id || item.swms_form_id}` : `${versionLabel}-${index}`,
      label: versionLabel,
      numericVersion: getFirstValue([
        item && item.versionNumber,
        item && item.version_number ? getNumericVersion(item.version_number) : null,
        getNumericVersion(versionLabel)
      ], index + 1),
      status,
      createdAt,
      createdBy,
      note,
      isCurrent: item && item.isCurrent === true,
      raw: item
    };
  });

  return normalizedItems.sort((a, b) => {
    const aVersion = Number(a.numericVersion ? a.numericVersion : 0);
    const bVersion = Number(b.numericVersion ? b.numericVersion : 0);
    return bVersion - aVersion;
  });
};

export default function SwmsVersionHistoryModal({ swms, jobId, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const controller = useRef();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState("");
  const [selectedVersionKey, setSelectedVersionKey] = useState("");

  const formName = swms && swms.swms_form_name ? swms.swms_form_name : "SWMS";
  const formNumber = swms && swms.swms_number ? swms.swms_number : "-";

  const fetchVersions = () => {
    if (!swms || !swms.swms_form_id || !jobId) {
      setLoading(false);
      setError(Strings.generic_error);
      return;
    }

    if (controller.current) {
      controller.current.abort();
    }

    controller.current = new AbortController();
    setLoading(true);
    setError("");

    dispatch(getSwmsDraftVersionsV2({ signal: controller.current.signal }, jobId, swms.swms_form_id)).then((response) => {
      const items = normalizeVersions(toArray(response));
      setVersions(items);
      setLoading(false);
    }).catch((err) => {
      setLoading(false);
      setError(err ? `${err}` : Strings.generic_error);
    });
  };

  useEffect(() => {
    fetchVersions();

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, [dispatch, jobId, swms]);

  useEffect(() => {
    setSelectedVersionKey("");
  }, [versions]);

  const currentVersion = useMemo(() => {
    if (!versions || versions.length === 0) {
      return null;
    }

    const explicitCurrent = versions.find((item) => item.isCurrent === true);
    if (explicitCurrent) {
      return explicitCurrent;
    }

    const currentFromStatus = versions.find((item) => {
      const normalizedStatus = normalizeStatus(item.status);
      return normalizedStatus === "DRAFT" || normalizedStatus === "IN_PROGRESS" || normalizedStatus === "PENDING" || normalizedStatus === "PENDING_APPROVAL" || normalizedStatus === "APPROVED";
    });

    return currentFromStatus ? currentFromStatus : versions[0];
  }, [versions]);

  const statusText = currentVersion ? formatStatusLabel(normalizeStatus(currentVersion.status)) : "Unknown";
  const statusClass = currentVersion ? getStatusClass(currentVersion.status) : styles.statusDraft;

  const selectedVersion = useMemo(() => {
    if (!selectedVersionKey || versions.length === 0) {
      return null;
    }

    const matchedVersion = versions.find((item) => item.key === selectedVersionKey);
    return matchedVersion ? matchedVersion : null;
  }, [selectedVersionKey, versions]);

  const selectedOlderVersion = useMemo(() => {
    if (!selectedVersion || !currentVersion) {
      return null;
    }

    if (selectedVersion.key === currentVersion.key) {
      return null;
    }

    return selectedVersion;
  }, [selectedVersion, currentVersion]);

  const selectedOlderVersionNumber = useMemo(() => {
    if (!selectedOlderVersion) {
      return null;
    }

    const numericVersion = Number(selectedOlderVersion.numericVersion);
    if (!Number.isNaN(numericVersion) && numericVersion > 0) {
      return numericVersion;
    }

    const parsedVersion = getNumericVersion(selectedOlderVersion.label);
    if (parsedVersion === null || Number.isNaN(parsedVersion) || parsedVersion <= 0) {
      return null;
    }

    return parsedVersion;
  }, [selectedOlderVersion]);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !loading && !submitting) {
      onClose();
    }
  };

  const handleRetry = () => {
    fetchVersions();
  };

  const handleClose = () => {
    if (loading || submitting) {
      return;
    }

    onClose();
  };

  const handleVersionSelect = (version) => {
    if (!version || !currentVersion || loading || submitting) {
      return;
    }

    if (version.key === currentVersion.key) {
      return;
    }

    setSelectedVersionKey((prev) => (prev === version.key ? "" : version.key));
  };

  const handleCancelSelection = () => {
    if (submitting) {
      return;
    }

    setSelectedVersionKey("");
  };

  const handleRevertSelection = () => {
    if (submitting || !selectedOlderVersion || !selectedOlderVersionNumber || !swms || !swms.swms_form_id || !jobId) {
      return;
    }

    setSubmitting(true);
    dispatch(revertSwmsDraftVersionV2(jobId, swms.swms_form_id, selectedOlderVersionNumber)).then((message) => {
      setSubmitting(false);
      notification.success({
        message: Strings.success_title,
        description: message || "Version reverted successfully.",
        className: "ant-success"
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    }).catch((err) => {
      setSubmitting(false);
      notification.error({
        message: Strings.error_title,
        description: err || Strings.generic_error,
        className: "ant-error"
      });
    });
  };

  useEffect(() => {
    if (error) {
      notification.error({
        message: Strings.error_title,
        description: error,
        className: "ant-error"
      });
    }
  }, [error]);

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIconWrap}>
              <HistoryIcon />
            </span>
            <div className={styles.headerText}>
              <h3>Version History</h3>
              <p className={styles.headerSubtitle}>{formName} · {formNumber}</p>
            </div>
          </div>
          <button
            className={styles.closeBtn}
            onClick={handleClose}
            disabled={loading || submitting}
            type="button"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {!loading && (
          <div className={styles.currentBanner}>
            <div className={styles.currentBadge}>
              Current: {currentVersion ? currentVersion.label : "-"}
            </div>
            <span className={`${styles.statusBadge} ${statusClass}`}>
              {statusText}
            </span>
          </div>
        )}

        <div className={styles.body}>
          {loading && (
            <div className={styles.loadingState}>
              <CircularProgress size={24} />
              <p>Loading version history...</p>
            </div>
          )}

          {!loading && error && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button type="button" className={styles.retryBtn} onClick={handleRetry}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div className={styles.emptyState}>
              <p>No version history available yet.</p>
            </div>
          )}

          {!loading && !error && versions.length > 0 && (
            <div className={styles.versionList}>
              {versions.map((version) => {
                const isCurrentVersion = currentVersion && currentVersion.key === version.key;
                const isSelectable = !isCurrentVersion;
                const isSelected = selectedVersionKey === version.key;
                const versionItemClass = `${styles.versionItem} ${isCurrentVersion ? styles.versionItemCurrent : ""} ${isSelectable ? styles.versionItemSelectable : ""} ${isSelected ? styles.versionItemSelected : ""}`;

                return (
                  <button
                    key={version.key}
                    className={versionItemClass}
                    type="button"
                    onClick={() => handleVersionSelect(version)}
                    disabled={!isSelectable}
                  >
                    <div className={styles.versionTopRow}>
                      <div className={styles.versionTopLeft}>
                        <div className={styles.versionTitleWrap}>
                          <span className={styles.versionLabel}>{version.label}</span>
                          {isCurrentVersion && <span className={styles.currentPill}>Current</span>}
                        </div>
                        <span className={`${styles.statusBadge} ${getStatusClass(version.status)}`}>
                          {formatStatusLabel(normalizeStatus(version.status))}
                        </span>
                      </div>
                      {isSelectable && (
                        <span className={`${styles.versionRevertHint} ${isSelected ? styles.versionRevertHintSelected : ""}`}>
                          <RevertIcon />
                          Revert
                        </span>
                      )}
                    </div>
                    <div className={styles.versionMeta}>
                      <span>Created {formatDate(version.createdAt)}</span>
                      <span>&middot;</span>
                      <span>By {version.createdBy ? version.createdBy : "-"}</span>
                    </div>
                    <p className={styles.versionNote}>{version.note || getDefaultVersionNote(isCurrentVersion)}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!loading && !error && selectedOlderVersion && (
          <div className={styles.revertBar}>
            <div className={styles.revertMessageWrap}>
              <WarningIcon />
              <p className={styles.revertMessageText}>
                Reverting to <strong>{selectedOlderVersion.label}</strong> will replace the current live version. The current version will be archived and remain visible in history. This action cannot be undone automatically.
              </p>
            </div>
            <div className={styles.revertActions}>
              <button type="button" className={styles.revertCancelBtn} onClick={handleCancelSelection} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className={styles.revertConfirmBtn} onClick={handleRevertSelection} disabled={submitting}>
                {submitting ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <RevertIcon />}
                {submitting ? "Reverting..." : `Revert to ${selectedOlderVersion.label}`}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
